/**
 * Who you are, what the app looks like, and whether the socket is up.
 *
 * Server data - parties, friends, messages - lives in TanStack Query, not
 * here. This store is only for state the client owns.
 */

import { create } from 'zustand';
import { configure as configureSound } from '@/lib/sound';
import { loadCustomEmoji, type CustomEmoji } from '@/lib/emoji';
import { warmGameArt } from '@/lib/gameArt';
import { ApiError, api, endpoints, restoreToken, setToken, socket, token, type LoginResult, type SocketState } from '@/lib/api';
import type { Catalog, PublicUser, Settings } from '@/lib/types';
import { SCALES, fontById } from '@/lib/fonts';
import { SCENE_IDS } from '@/lib/themes';
import { applyScene } from '@/lib/wallpaper';
import { bridge } from '@/lib/bridge';

type SessionState = {
  me: PublicUser | null;
  settings: Settings | null;
  catalog: Catalog | null;
  /**
   * Custom emoji, held here rather than only in the module cache: a message
   * drawing :name: has to re-render when the set finally loads, and a plain
   * module variable cannot tell React that it changed.
   */
  emoji: CustomEmoji[];
  connection: SocketState;
  /** True while the server cannot be reached at all. */
  offline: boolean;
  ping: number | null;
  ready: boolean;
  booting: boolean;

  boot: () => Promise<void>;
  signIn: (username: string, password: string) => Promise<LoginResult>;
  signUp: (username: string, password: string, email: string, displayName?: string) => Promise<LoginResult>;
  /** Starts a session from a token that has already been earned. */
  adopt: (token: string, user: PublicUser) => Promise<void>;
  signOut: (everywhere?: boolean) => void;
  patchMe: (patch: Record<string, unknown>) => Promise<void>;
  /** Fetches your own account again, without the boot sequence around it. */
  refresh: () => Promise<void>;
  saveSettings: (patch: Partial<Settings>) => Promise<void>;
};

let shiftTimer = 0;
/** The last app size handed to the window, so a settings save that did not change it sends nothing. */
let sentZoom = 0;

/** Reads a number from settings or storage, clamped, with a fallback. */
/*
 * The eight variables an accent is made of, derived from one colour.
 *
 * The presets are eight hand-written custom properties each, in tokens.css.
 * A colour somebody picked out of a colour wheel has to produce the same
 * eight, and the only one that is not arithmetic is --accent-fg: the text
 * drawn ON the accent. Pick white for everything and a yellow accent gets
 * white-on-yellow buttons nobody can read, which is exactly the colour
 * people choose first.
 */
const ACCENT_VARS = [
  '--accent', '--accent-hi', '--accent-lo', '--accent-fg',
  '--accent-soft', '--accent-line', '--glow', '--glow-page'
];

/** Perceived brightness, 0 to 1. sRGB weights rather than a plain average,
    because green reads far brighter than blue at the same value. */
function luminance(hex: string): number {
  const n = parseInt(hex.slice(1), 16);
  const r = ((n >> 16) & 255) / 255;
  const g = ((n >> 8) & 255) / 255;
  const b = (n & 255) / 255;
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

function applyCustomAccent(root: HTMLElement, hex: string): void {
  /* The attribute has to go, or the preset rules would win the cascade over
     these - they are a more specific selector than the inline style is not,
     but leaving a stale attribute on is confusing for anything reading it. */
  delete root.dataset.accent;

  const mix = (pct: number, into: string) => 'color-mix(in srgb, ' + hex + ' ' + pct + '%, ' + into + ')';
  root.style.setProperty('--accent', hex);
  root.style.setProperty('--accent-hi', mix(82, '#ffffff'));
  root.style.setProperty('--accent-lo', mix(85, '#000000'));
  /* 0.45 rather than 0.5: white text on a mid colour is readable further
     down than black text is up, so the tipping point is not the middle. */
  root.style.setProperty('--accent-fg', luminance(hex) > 0.45 ? '#12131a' : '#ffffff');
  root.style.setProperty('--accent-soft', mix(10, 'transparent'));
  root.style.setProperty('--accent-line', mix(34, 'transparent'));
  root.style.setProperty('--glow', mix(20, 'transparent'));
  root.style.setProperty('--glow-page', mix(5, 'transparent'));
}

function clearCustomAccent(root: HTMLElement): void {
  for (const v of ACCENT_VARS) root.style.removeProperty(v);
}

function clampNum(raw: unknown, lo: number, hi: number, fallback: number): number {
  const n = Number(raw);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(lo, Math.min(hi, n));
}

/** Theme, density and scale live on <html> so they apply before first paint. */
export function applyPrefs(settings: Partial<Settings> | null): void {
  const root = document.documentElement;
  // Anything saved under an old id lands on midnight rather than leaving the
  // app with no palette at all. The list has to match the blocks in
  // tokens.css: a theme missing from here is silently ignored, and one
  // missing from there paints nothing.
  const THEMES = [
    'graphite', 'warm', 'midnight', 'obsidian', 'abyss', 'eclipse', 'ember', 'forest', 'orchid', 'light',
    'slate', 'carbon', 'amoled', 'nord', 'nightshade', 'ocean', 'crimson', 'sunset', 'rosewood',
    'paper', 'sky', 'mint', 'lavender', 'peach',
    /* The scenes carry a wallpaper as well as a palette, but as far as this
       function is concerned they are themes like any other. */
    ...SCENE_IDS
  ];
  /*
   * Graphite Green is the default for anyone who has not chosen, and the
   * fallback for a saved id this build no longer knows.
   *
   * Anyone who HAS chosen keeps their choice: a saved theme is read first. With
   * no accent chosen the theme's own colour is used, so the default really is
   * green rather than green under an indigo override.
   */
  const saved = settings?.theme ?? localStorage.getItem('pogu.theme') ?? 'graphite';
  const theme = THEMES.includes(saved) ? saved : 'graphite';
  const accent = settings?.accent ?? localStorage.getItem('pogu.accent') ?? 'theme';
  const density = settings?.density ?? localStorage.getItem('pogu.density') ?? 'comfortable';
  const scale = settings?.uiScale ?? localStorage.getItem('pogu.scale') ?? 'm';

  // Only crossfade when the palette actually changes - not on every settings
  // save, and not on the first paint, where there is nothing to fade from.
  if (root.dataset.theme && root.dataset.theme !== theme) {
    document.body.classList.add('theme-shift');
    clearTimeout(shiftTimer);
    shiftTimer = window.setTimeout(() => document.body.classList.remove('theme-shift'), 320);
  }

  root.dataset.theme = theme;
  /* The palette is on the page now, so the wallpaper can start: it holds the
     old picture until the new one has decoded, and does nothing at all for
     the flat themes. */
  applyScene(theme);
  // Violet is the :root default, so it needs no attribute at all.
  /* "theme" means the theme's own accent, so no override at all. */
  if (accent.startsWith('#')) applyCustomAccent(root, accent);
  else {
    clearCustomAccent(root);
    if (accent && accent !== 'violet' && accent !== 'theme') root.dataset.accent = accent;
    else delete root.dataset.accent;
  }
  localStorage.setItem('pogu.accent', accent);
  root.dataset.density = density;

  /* The four Appearance sliders, and the two switches beside them. */
  const contrast = clampNum(settings?.contrast ?? localStorage.getItem('pogu.contrast'), 1, 5, 3);
  /*
   * Corner Style, and only Corner Style.
   *
   * Every scene has its own corner feel and says so in its stylesheet; an
   * inline style beats a stylesheet, so this is written on the element only
   * when somebody has actually moved that slider. It is deliberately not
   * mirrored into storage either - a mirror written on every save is
   * indistinguishable from a choice on the next start, which is exactly how
   * a theme's corners would get pinned to 1 for everybody forever.
   */
  const savedRadius = settings?.cornerRadius;
  const radiusMul = clampNum(savedRadius, 0, 2, 1);
  const shadowMul = clampNum(settings?.shadowDepth ?? localStorage.getItem('pogu.shadow'), 0, 2, 1);
  root.dataset.contrast = String(contrast);
  root.dataset.shadow = '1';
  if (savedRadius === undefined || savedRadius === null) root.style.removeProperty('--radius-mul');
  else root.style.setProperty('--radius-mul', String(radiusMul));
  root.style.setProperty('--shadow-mul', String(shadowMul));
  localStorage.setItem('pogu.contrast', String(contrast));
  localStorage.removeItem('pogu.radius');
  localStorage.setItem('pogu.shadow', String(shadowMul));

  document.body.classList.toggle('no-illustration', settings?.illustration === false);
  document.body.classList.toggle('high-contrast', !!settings?.highContrast);
  /* Controller and couch: larger targets, and a focus ring that is always
     visible rather than only after a Tab - a controller cannot hover. */
  document.body.classList.toggle('big-targets', !!settings?.bigTargets);
  root.dataset.scale = scale;
  /*
   * The size of everything.
   *
   * In the desktop app this is Chromium's own page zoom. It used to be CSS
   * `zoom` on the page, which scaled what you saw but not what the layout
   * measured: breakpoints still read the unzoomed window, and every pop-up
   * placed from a measured position landed off by the zoom - the notification
   * panel half off the right edge at 120%. Page zoom changes the size of the
   * window the page believes it has, so all of that agrees, and the window's
   * minimum size grows with it so nothing gets cut off.
   *
   * A browser tab has its own zoom (Ctrl + and Ctrl -), which already works
   * this way, so the page leaves it alone there.
   */
  root.style.removeProperty('zoom');
  const zoom = SCALES.find((s) => s.id === scale)?.zoom ?? 1;
  if (zoom !== sentZoom) { sentZoom = zoom; void bridge()?.setZoom?.(zoom); }
  /* Message text has its own size on top of that. */
  root.style.setProperty('--chat-fs', clampNum(settings?.chatFontSize ?? localStorage.getItem('pogu.chatFs'), 12, 24, 16) + 'px');
  localStorage.setItem('pogu.chatFs', String(clampNum(settings?.chatFontSize ?? localStorage.getItem('pogu.chatFs'), 12, 24, 16)));
  root.lang = settings?.appLanguage ?? 'en';
  localStorage.setItem('pogu.theme', theme);
  localStorage.setItem('pogu.density', density);
  localStorage.setItem('pogu.scale', scale);

  configureSound({
    enabled: settings?.sounds !== false,
    volume: settings?.soundVolume ?? 0.5
  });

  /*
   * Appearance, part two.
   *
   * Each of these is a CSS variable rather than a class, so one value set
   * here reaches every place that reads it without a rule per component.
   */

  // Round, squircle, or square - applied to every avatar at once.
  const shape = settings?.avatarShape ?? localStorage.getItem('pogu.avatarShape') ?? 'round';
  root.style.setProperty('--avatar-radius', shape === 'square' ? '18%' : shape === 'soft' ? '32%' : '50%');
  localStorage.setItem('pogu.avatarShape', shape);

  // The typeface. Inter is the design; the others are for people who cannot
  // read it comfortably, which is a real thing and not a preference.
  const font = fontById(settings?.fontFamily ?? localStorage.getItem('pogu.font') ?? 'inter');
  root.style.setProperty('--font', font.stack);
  localStorage.setItem('pogu.font', font.id);

  // How far apart messages sit, on top of the compact switch.
  const msgGap = clampNum(settings?.messageSpacing ?? localStorage.getItem('pogu.msgGap'), 0, 2, 1);
  root.style.setProperty('--msg-gap-mul', String(msgGap));
  localStorage.setItem('pogu.msgGap', String(msgGap));

  // How big an emoji-only message is drawn.
  const bigEmoji = clampNum(settings?.emojiScale ?? localStorage.getItem('pogu.emojiScale'), 20, 64, 34);
  root.style.setProperty('--emoji-big', bigEmoji + 'px');
  localStorage.setItem('pogu.emojiScale', String(bigEmoji));

  document.body.classList.toggle('hide-chat-avatars', settings?.chatAvatars === false);
  document.body.classList.toggle('plain-names', settings?.colorNames === false);

  document.body.classList.toggle('reduce-motion', !!settings?.reduceMotion);
  document.body.classList.toggle('stream-mode', !!settings?.streamMode);
}


/**
 * Whether the server answered and said no, as opposed to not answering.
 *
 * A 401 means the token is dead and the session should end. A failed fetch
 * means the network is down, and throwing the session away for that would
 * make every dropped connection cost a sign-in.
 */
function rejected(err: unknown): boolean {
  return err instanceof ApiError && err.status >= 400 && err.status < 500;
}

/**
 * Keeps trying until the server is back.
 *
 * Backs off to half a minute rather than hammering a box that is down, and
 * runs at most one loop at a time however many times boot() fails.
 */
let retrying: ReturnType<typeof setTimeout> | null = null;
let backoff = 2000;

function retryUntilOnline(): void {
  // boot() calls this every time it fails, so one loop is already running.
  if (retrying) return;
  retrying = setTimeout(() => {
    retrying = null;
    backoff = Math.min(30_000, Math.round(backoff * 1.6));
    // Failing again schedules the next attempt from inside boot().
    void useSession.getState().boot();
  }, backoff);
}

/** Back to trying quickly, now that the server has answered once. */
function onlineAgain(): void {
  backoff = 2000;
  if (retrying) { clearTimeout(retrying); retrying = null; }
}

export const useSession = create<SessionState>((set, get) => ({
  me: null,
  settings: null,
  catalog: null,
  emoji: [],
  connection: 'idle',
  offline: false,
  ping: null,
  ready: false,
  booting: true,

  async boot() {
    set({ booting: true });
    try {
      const catalog = await endpoints.catalog();
      set({ catalog, offline: false });
      onlineAgain();
      // Custom emoji are needed to draw :name: in messages, not just to pick
      // them, so they load at boot rather than when the picker first opens.
      void loadCustomEmoji().then((emoji) => set({ emoji }));
      // The games screen is twenty banners at once. Fetch them while nothing
      // is happening, so opening it is not twenty requests at once.
      warmGameArt();
    } catch {
      /*
       * The server is not answering at all.
       *
       * Carrying on used to land you on the sign-in screen with a form that
       * could not work and no explanation. There is nothing useful to show
       * without a server, so the app holds on the loading screen and keeps
       * trying - see retryUntilOnline below.
       */
      set({ offline: true, booting: false, ready: true });
      retryUntilOnline();
      return;
    }

    /*
     * The desktop keeps its own copy of the token, so ask for that before
     * concluding nobody is signed in.
     */
    if (!(await restoreToken())) {
      set({ booting: false, ready: true });
      return;
    }

    try {
      const { user, settings } = await endpoints.me();
      applyPrefs(settings);
      set({ me: user, settings });
      socket.connect();
    } catch (err) {
      /*
       * A refused token and an unreachable server are different failures.
       *
       * Both used to sign you out, so one dropped connection lost the
       * session and you had to type your password again. Only an actual
       * rejection clears the token now.
       */
      if (rejected(err)) {
        setToken(null);
        set({ me: null, settings: null });
      } else {
        set({ offline: true, booting: false, ready: true });
        retryUntilOnline();
        return;
      }
    }
    set({ booting: false, ready: true });
  },

  /*
   * What your account is allowed can change while you are signed in - an
   * administrator moving what Premium is worth, or your own membership being
   * set. Both arrive as a socket event, and this is how the screen catches
   * up without a reload.
   */
  async refresh() {
    if (!token()) return;
    try {
      const { user, settings } = await endpoints.me();
      applyPrefs(settings);
      set({ me: user, settings });
    } catch {
      /* the old answer stays until the next attempt */
    }
  },

  /**
   * Signs in, unless the account wants a second factor.
   *
   * Returns the server's answer either way. With two-factor on there is no
   * token yet - only a ticket - so the caller gets that back and asks for the
   * code rather than this store pretending somebody is signed in.
   */
  async signIn(username, password) {
    const res = await endpoints.login(username, password);
    if (res.token && res.user) await useSession.getState().adopt(res.token, res.user);
    return res;
  },

  async signUp(username, password, email, displayName) {
    const res = await endpoints.register(username, password, email, displayName);
    if (res.token && res.user) await useSession.getState().adopt(res.token, res.user);
    return res;
  },

  /**
   * Takes a token and starts the session behind it.
   *
   * The last step of every way in - password, second factor, or a password
   * reset - so the socket, the preferences and the stored token are set up in
   * exactly one place.
   */
  async adopt(t, user) {
    setToken(t);
    const { settings } = await endpoints.me();
    applyPrefs(settings);
    set({ me: user, settings, offline: false });
    socket.connect();

    /*
     * Signing in lands on Home, always.
     *
     * Whatever was in the address bar belonged to whoever was signed in
     * before - a party they were in, a profile they were reading. Dropping
     * somebody into the previous session's last screen is disorienting at
     * best and shows them a party they are not in at worst.
     */
    if (location.hash && location.hash !== '#/home') location.hash = '#/home';
  },

  signOut(everywhere) {
    // Tell the server first, but never let a failed request strand you in a
    // session you asked to end - the local teardown happens either way.
    const path = everywhere ? '/api/auth/logout-all' : '/api/auth/logout';
    void api.post(path).catch(() => {});
    socket.close();
    setToken(null);
    set({ me: null, settings: null, connection: 'idle', ping: null });
  },

  async patchMe(patch) {
    /*
     * Applied to the copy in hand first.
     *
     * Picking a frame used to mean a click, a round trip, and only then a
     * frame - which on a slow link reads as the click having missed. The
     * change lands now and the server`s answer replaces it a moment later;
     * if the server refuses, the old value goes back and the caller`s catch
     * puts the reason on screen.
     */
    const before = get().me;
    if (before) set({ me: { ...before, ...patch } as typeof before });

    try {
      const { user } = await endpoints.updateMe(patch);
      set({ me: user });
    } catch (err) {
      if (before) set({ me: before });
      throw err;
    }
  },

  async saveSettings(patch) {
    const current = get().settings;
    // Applied straight away so the interface responds to the click, then
    // reconciled with whatever the server accepted.
    if (current) {
      const optimistic = { ...current, ...patch };
      applyPrefs(optimistic);
      set({ settings: optimistic });
    }
    const { settings } = await endpoints.updateSettings(patch);
    applyPrefs(settings);
    set({ settings });
  }
}));

/* ---- socket wiring, set up once from main.tsx ---- */

export function wireSession(): void {
  socket.on('state', (next: SocketState) => useSession.setState({ connection: next }));

  /*
   * The server`s view of you, the moment the socket is up.
   *
   * This was sent and nobody was listening, so your own account stayed
   * exactly as the sign-in response left it - including status, which is
   * whatever was written when you last disconnected. That is why you sat
   * there marked offline while everybody else could see you were on.
   */
  socket.on('ready', (data: { user?: PublicUser }) => {
    if (!data?.user) return;
    const me = useSession.getState().me;
    useSession.setState({ me: me ? { ...me, ...data.user } : data.user });
  });

  /* Presence for anybody, including yourself. */
  socket.on('presence', (d: { userId: string; status?: PublicUser['status']; customStatus?: string }) => {
    const me = useSession.getState().me;
    if (!me || d.userId !== me.id) return;
    useSession.setState({
      me: { ...me, status: d.status ?? me.status, customStatus: d.customStatus ?? me.customStatus }
    });
  });

  socket.on('account:updated', (data: { user: PublicUser }) => {
    useSession.setState({ me: data.user });
  });

  socket.on('user:updated', (data: { user: PublicUser }) => {
    const me = useSession.getState().me;
    if (me && data.user.id === me.id) useSession.setState({ me: { ...me, ...data.user } });
  });

  // Round trip on the socket that carries everything else, which is the
  // latency that actually matters - not an HTTP request.
  let sentAt = 0;
  let timer: ReturnType<typeof setInterval> | null = null;

  socket.on('pong', () => {
    if (!sentAt) return;
    useSession.setState({ ping: Math.max(1, Math.round(performance.now() - sentAt)) });
    sentAt = 0;
  });

  socket.on('state', (next: SocketState) => {
    if (timer) clearInterval(timer);
    if (next !== 'open') { useSession.setState({ ping: null }); return; }
    const beat = () => { sentAt = performance.now(); socket.send('ping'); };
    beat();
    timer = setInterval(beat, 8000);
  });
}
